/**
 * ErrorState — message + retry button with error-map resolution.
 *
 * Relocated from legacy.tsx by T312 (FR-015).
 * When `errorSignal` is provided, resolves user-facing copy from the
 * central error map via `errorMessage()`.
 *
 * Uses CSS custom-property references for spacing and layout.
 */
import React from "react";
import type { ReactNode, CSSProperties } from "react";
import { errorMessage } from "../../api/errorMap";

export interface ErrorStateProps {
  message?: string;
  /** When provided, resolves user-facing copy from the central error map. */
  errorSignal?: string;
  onRetry?: () => void;
  variant?: "inline" | "full";
  children?: ReactNode;
}

export function ErrorState({ message, errorSignal, onRetry, variant, children }: ErrorStateProps) {
  // Resolve copy from the central error map when errorSignal is provided
  const resolved = errorSignal ? errorMessage(errorSignal) : undefined;
  const displayTitle = resolved?.title;
  const displayBody = resolved?.body ?? message;

  const style: CSSProperties = {
    display: "flex",
    flexDirection: variant === "inline" ? "row" : "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--space-md, 12px)",
    padding: variant === "inline" ? "var(--space-sm, 8px) var(--space-md, 12px)" : "var(--space-2xl, 32px) var(--space-lg, 16px)",
    textAlign: "center",
  };

  return (
    <div role="alert" data-testid="error-state" style={style}>
      {children ?? (
        <>
          {displayTitle && <p style={{ fontWeight: 600 }}>{displayTitle}</p>}
          {displayBody && <p>{displayBody}</p>}
        </>
      )}
      {resolved?.recoveryAction && (
        <a href={resolved.recoveryAction.href} onClick={resolved.recoveryAction.onClick}>
          {resolved.recoveryAction.label}
        </a>
      )}
      {onRetry && (
        <button onClick={onRetry} type="button">
          Try again
        </button>
      )}
    </div>
  );
}
